import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import FocusLock from 'react-focus-lock';

import Container from '~/layout/Container';

import Icon from '~/components/base/Icon';

import useScrollLock from '~/hooks/useScrollLock';

import { isBrowser, hexToRgba } from '~/utilities/helpers';

import { transition } from '~/styles/transition';

const Wrapper = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    z-index: ${({ theme }) => theme.zIndex.modal};
    width: 100%;
    height: 100%;
    background-color: ${({ theme }) =>
        hexToRgba(theme.colors.text.lightBlue, 0.95)};
    overflow: scroll;
`;

const ModalContent = styled(Container)`
    margin-top: 120px;
`;

export const CloseButton = styled.button`
    position: absolute;
    display: flex;
    align-items: center;
    gap: 10px;
    top: 25px;
    right: 35px;
    color: ${({ theme }) => theme.colors.accent.blue};
    font-family: ${({ theme }) => theme.fonts.heading};
    font-weight: ${({ theme }) => theme.fontWeights.black};
    font-size: 2rem;
    text-transform: uppercase;
    ${transition({}, 'color')};

    i {
        position: relative;
        top: -3px;
    }

    svg * {
        ${transition({}, 'color')};
    }

    &:hover,
    &:focus {
        text-decoration: underline;
        color: ${({ theme }) => theme.colors.primary.yellow};

        svg * {
            color: ${({ theme }) => theme.colors.primary.yellow};
        }
    }
`;

const Modal = ({ open, setOpen, onClose = () => {}, children }) => {
    const modalRef = useRef(null);
    const { setIsLocked } = useScrollLock(modalRef);

    const onEscapeKeyDown = (e) => {
        if (e.key === 'Escape') {
            setOpen(false);
        }
    };

    useEffect(() => {
        setIsLocked(open);
    }, [open, setIsLocked]);

    const closeModal = () => {
        setOpen(false);

        if (onClose) {
            onClose();
        }
    };

    return isBrowser()
        ? createPortal(
              open ? (
                  <FocusLock returnFocus={true}>
                      <Wrapper
                          aria-live="polite"
                          tabIndex={0}
                          onKeyDown={onEscapeKeyDown}
                          ref={modalRef}
                      >
                          <ModalContent>
                              <CloseButton onClick={closeModal}>
                                  Close
                                  <Icon icon="close" size={20} color="blue" />
                              </CloseButton>
                              {children}
                          </ModalContent>
                      </Wrapper>
                  </FocusLock>
              ) : null,
              document.body
          )
        : null;
};

Modal.propTypes = {
    open: PropTypes.bool.isRequired,
    setOpen: PropTypes.func.isRequired,
    onClose: PropTypes.func,
    children: PropTypes.node.isRequired
};

export default Modal;
